import { useState, type ChangeEvent, type MouseEvent } from "react";
import { useControlled } from "@mui/material/utils";

import { fieldDescribedBy } from "ui/forms/field-message/field-message.helpers";
import { useFieldId } from "ui/forms/use-field-id";
import {
  clamp,
  formatNumber,
  inputModeFor,
  isNumberDraft,
  parseNumber,
  shouldShowSuffix,
  stepValue,
  type StepDirection,
} from "ui/number-field/number-field.helpers";
import { type NumberFieldProps } from "ui/number-field/number-field.types";

type UseNumberFieldProps = Pick<
  NumberFieldProps,
  | "id"
  | "name"
  | "value"
  | "defaultValue"
  | "onValueChange"
  | "min"
  | "max"
  | "step"
  | "disabled"
  | "readOnly"
  | "errMessage"
  | "helperText"
  | "suffix"
  | "onBlur"
  | "onKeyDown"
  | "data-testid"
>;

export const useNumberField = ({
  id,
  name,
  value: valueProp,
  defaultValue = null,
  onValueChange,
  min,
  max,
  step = 1,
  disabled,
  readOnly,
  errMessage,
  helperText,
  suffix,
  onBlur,
  onKeyDown,
  "data-testid": dataTestId,
}: UseNumberFieldProps) => {
  const fieldId = useFieldId(id, name);
  const [value, setValue] = useControlled<number | null>({
    controlled: valueProp,
    default: defaultValue,
    name: "NumberField",
  });
  // What is on screen while it is being typed: `-` and `1.` are on their way to a number, and
  // re-deriving the text from the value would swallow them.
  const [draft, setDraft] = useState<string | null>(null);

  const text = draft ?? formatNumber(value);
  const isLocked = !!disabled || !!readOnly;

  const commit = (next: number | null) => {
    setDraft(null);
    if (next !== value) {
      setValue(next);
      onValueChange?.(next);
    }
  };

  const handleChange = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const nextText = event.target.value;
    if (!isNumberDraft(nextText)) {
      return;
    }
    setDraft(nextText);
    const parsed = parseNumber(nextText);
    if (parsed !== undefined && parsed !== value) {
      setValue(parsed);
      onValueChange?.(parsed);
    }
  };

  const handleBlur: NonNullable<NumberFieldProps["onBlur"]> = (event) => {
    if (draft !== null) {
      const parsed = parseNumber(draft);
      // A draft that never became a number (`-`) gives way to the last value it had.
      commit(parsed === undefined ? value : parsed === null ? null : clamp(parsed, min, max));
    }
    onBlur?.(event);
  };

  const stepBy = (direction: StepDirection) => {
    const typed = draft === null ? undefined : parseNumber(draft);
    commit(stepValue(typed === undefined ? value : typed, step, direction, min, max));
  };

  const increment = () => stepBy(1);
  const decrement = () => stepBy(-1);

  const handleKeyDown: NonNullable<NumberFieldProps["onKeyDown"]> = (event) => {
    if (!isLocked && (event.key === "ArrowUp" || event.key === "ArrowDown")) {
      // The caret would otherwise jump to the start or the end of the text.
      event.preventDefault();
      stepBy(event.key === "ArrowUp" ? 1 : -1);
    }
    onKeyDown?.(event);
  };

  // The buttons sit inside the field: a plain press would take focus off the input before the click
  // ran, committing the draft and dropping the focus ring mid-adjustment.
  const handleStepperMouseDown = (event: MouseEvent) => event.preventDefault();

  return {
    fieldId,
    text,
    showSuffix: shouldShowSuffix(suffix, text),
    canIncrement: !isLocked && (max === undefined || value === null || value < max),
    canDecrement: !isLocked && (min === undefined || value === null || value > min),
    increment,
    decrement,
    handleChange,
    handleBlur,
    handleKeyDown,
    handleStepperMouseDown,
    spinbuttonProps: {
      role: "spinbutton",
      // Read off the text on screen, not the last reported value: a draft (`-`) or a value the caller has not
      // taken yet would otherwise be announced as a number the reader cannot see.
      "aria-valuenow": (draft === null ? value : parseNumber(draft)) ?? undefined,
      "aria-valuemin": min,
      "aria-valuemax": max,
      inputMode: inputModeFor(step),
    } as const,
    describedBy: fieldDescribedBy(fieldId, { errorMessage: errMessage, helperText }),
    testId: dataTestId ?? `${name ?? fieldId}-number-input`,
  };
};
